import React from 'react';
import { RobbieMark, SegmentedProgress } from '../ui';
import type { DashboardRFQ } from '../../hooks/useDashboard';
import type { RowResearch } from '../../hooks/useResearchProgress';

/** Shared cells for the desktop table and the phone card list. */

const STAGE_TAG_LABEL: Record<number, string> = {
    1: 'Upload BOM', 2: 'Criteria', 3: 'Discovery', 4: 'Outreach', 5: 'Quotes review',
};
const RAMP = ['bg-pipeline-1', 'bg-pipeline-2', 'bg-pipeline-3', 'bg-pipeline-4', 'bg-pipeline-5'];
export const TOTAL_STEPS = 9; // 5 live stages + 4 locked — in-flight progress never claims the locked ones

export const timeAgo = (iso: string): string => {
    const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
};

/** "about 15 min left" from the server ETA; null when the server has none. */
export const etaText = (r: RowResearch): string | null => {
    const eta = r.etaMinutes;
    if (!eta) return null;
    if (eta.max <= 1) return 'about a minute left';
    if (eta.min === eta.max || eta.min <= 0) return `about ${eta.max} min left`;
    return `${eta.min}–${eta.max} min left`;
};

export const researchLine = (r: RowResearch): string =>
    [`Researching · ${r.done} of ${r.total} done`, etaText(r)].filter(Boolean).join(' · ');

export const ResearchingTag: React.FC = () => (
    <span className="inline-flex items-center gap-[4px] whitespace-nowrap rounded-[6px] border border-robbie-border bg-ai-chip py-[2px] pl-[2px] pr-s3 text-[11.5px] font-semibold text-robbie-ink">
        <RobbieMark size="xxs" state="working" aria-label="Robbie is researching" />
        Researching
    </span>
);

export const StageTag: React.FC<{ row: DashboardRFQ; research?: RowResearch }> = ({ row, research }) => {
    if (research) return <ResearchingTag />;
    const cls = row.stageStep >= 4
        ? 'bg-action-bg border-action-border text-action-deep'
        : 'bg-neutral-bg border-neutral-border text-neutral-ink';
    return (
        <span className={`whitespace-nowrap rounded-[6px] border px-s3 py-[3px] text-[11.5px] font-semibold ${cls}`}>
            {STAGE_TAG_LABEL[row.stageStep]}
        </span>
    );
};

/** The research itself while it runs: done / researching / waiting, and the server ETA. */
export const ResearchProgress: React.FC<{ research: RowResearch }> = ({ research }) => {
    const eta = etaText(research);
    return (
        <span className="flex w-full flex-col gap-[5px]" title={researchLine(research)}>
            <SegmentedProgress
                counts={{ done: research.done, running: research.running, waiting: research.waiting }}
                showLegend={false}
            />
            <span className="text-[11.5px] leading-[1.35] text-ink-secondary">
                <b className="font-semibold text-ink">
                    <span className="font-mono">{research.done}</span> of <span className="font-mono">{research.total}</span> done
                </b>
                {eta && <><br />{eta}</>}
            </span>
        </span>
    );
};

/** Honest n/9: completed-through step over the full 9-stage journey — the
 * locked stages are never claimed as done. */
export const Progress: React.FC<{ row: DashboardRFQ; research?: RowResearch }> = ({ row, research }) => {
    if (research) return <ResearchProgress research={research} />;
    return (
        <span className="flex w-full items-center gap-[7px]">
            <span className="h-[4px] flex-1 overflow-hidden rounded-round bg-line-divider-soft">
                <span
                    className={`block h-full ${RAMP[row.stageStep - 1]}`}
                    style={{ width: `${(row.stageStep / TOTAL_STEPS) * 100}%` }}
                />
            </span>
            <span className="font-mono text-[11px] text-ink-muted">{row.stageStep}/{TOTAL_STEPS}</span>
        </span>
    );
};
